import { GEN5 } from './gen5'
import type { HumanoidModel } from './model'

/**
 * Das Roboterverzeichnis.
 *
 * Ein Eintrag beschreibt eine Maschine: den Zahlenstand, mit dem gerechnet
 * wird, den Körper, mit dem sie gezeichnet wird, und — wenn es eines gibt —
 * ihr 3D-Modell. Heute steht hier eine Maschine, der Gen5. Eine zweite
 * anzulegen heisst: ein zweites `HumanoidModel` schreiben und hier darauf
 * zeigen. Der Rechenkern ändert sich dabei nicht.
 *
 * `differsInModel()` und `sharingModel()` bleiben, obwohl sie mit einem
 * Eintrag trivial sind: ein Umschalter, der mal die Darstellung und mal die
 * Rechnung ändert, ohne dass man es sieht, ist die unangenehmste Sorte
 * Bedienelement — die Frage „rechnet das anders?" muss beantwortbar bleiben,
 * sobald ein zweiter Eintrag kommt.
 */

/**
 * Welcher Körper gezeichnet wird. Reine Darstellung.
 *
 * `gen5`: das Oberflächenmodell des Gen5, in Bauteile je Gelenk zerlegt
 * (`components/stage/figure/gen5.ts`).
 */
export type RobotBody = 'gen5'

export interface Robot {
  id: string
  name: string
  note: string
  /** Der Zahlenstand, mit dem gerechnet wird. */
  model: HumanoidModel
  body: RobotBody
  /**
   * Das 3D-Modell dieser Maschine (GLB), wenn es eines gibt.
   *
   * Die Hintergrundbühne zeigt es an Stelle der gerechneten Figur; das
   * Bühnenfenster zeigt immer die Figur, weil Gang, Marken und Hüllkörper an
   * ihr hängen. Ohne Pfad steht überall die Figur.
   */
  modelUrl?: string
}

export const ROBOTS: Record<string, Robot> = {
  /*
    Gen5, Stand V0_24 (`gen5.ts`). Im Bühnenfenster läuft sein GLB, in
    Bauteile je Gelenk zerlegt (`public/models/gen5-figure.glb`); auf der
    Hintergrundbühne steht das ungeteilte Modell (`public/models/gen5.glb`,
    Draco, aus dem Neura-3D-Viewer: 699.198 Dreiecke).
  */
  gen5: {
    id: 'gen5',
    name: 'Gen5 · V0_24',
    note: 'Kinematikstand V0_24 (10/2026): CAD-Gelenkpunkte, Grenzwinkel, Massenmodell 55 kg, Momente aus der Antriebsauslegung. Hüftkette im CAD Nicken → Rollen → Gieren.',
    model: GEN5,
    body: 'gen5',
    modelUrl: '/models/gen5.glb',
  },
}

export const DEFAULT_ROBOT = 'gen5'

/**
 * Rechnen zwei Einträge unterschiedlich?
 *
 * Verglichen wird die Identität des Modells, nicht sein Inhalt — zwei Einträge
 * auf demselben Objekt rechnen garantiert gleich, und zwei verschiedene Objekte
 * gelten als verschieden, auch wenn sie zufällig dieselben Zahlen tragen. Für
 * die Frage „darf ich diese beiden Ergebnisse nebeneinanderlegen?" ist das die
 * sichere Richtung des Irrtums.
 */
export function differsInModel(a: string, b: string): boolean {
  const ra = ROBOTS[a]
  const rb = ROBOTS[b]
  if (!ra || !rb) return true
  return ra.model !== rb.model
}

/** Alle Einträge, die auf demselben Zahlenstand rechnen. */
export function sharingModel(id: string): Robot[] {
  const ref = ROBOTS[id]
  if (!ref) return []
  return Object.values(ROBOTS).filter((r) => r.model === ref.model)
}
